import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileSignature, ExternalLink, Loader2, Inbox, Package, Download, Sparkles, AlertOctagon } from "lucide-react";
import HeaderBar from "../components/HeaderBar";
import { useAuth } from "../context/AuthContext";
import { listMyTosAcceptances, listMyDesigns, requestArchitectPackage, deleteMyAccount, previewAccountDeletion } from "../lib/api";
import { toast } from "sonner";
import ProjectDeliveryDashboard from "../components/ProjectDeliveryDashboard";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";

/**
 * V2.0.21 — Account page (`/account`).
 *
 * Surfaces the logged-in user's:
 *   • Identity card (name, email, avatar, ToS version they last accepted)
 *   • Full ToS-acceptance audit history (most-recent first) — every row
 *     is the same audit trail counsel sees, but scoped to the caller.
 *     Useful for users who want to give a record to their licensed
 *     architect before construction.
 *
 * Endpoints used:
 *   GET /api/tos/acceptances  → rows[]
 */
const SURFACE_LABEL = {
  lightbox_download: "PDF / IFC download",
  stripe_checkout: "Studio-tier package",
  ai_renders_checkout: "AI Renders",
  signoff_checkout: "Professional sign-off",
};

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Account() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  // V2.0.23 — Architect-ready package banner state.
  const [designs, setDesigns] = useState(null);
  const [selectedDesignId, setSelectedDesignId] = useState("");
  const [pkgBuilding, setPkgBuilding] = useState(false);
  // V2.0.28 — when set, we render the Project Delivery Dashboard
  // instead of the regular Account body. Holds { designName, designId,
  // filename, zipBlobUrl, sizeBytes, contents, remainingCredits }.
  const [deliveryPackage, setDeliveryPackage] = useState(null);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    try {
      const [acc, des] = await Promise.all([
        listMyTosAcceptances(),
        listMyDesigns(),
      ]);
      const list = des.data || [];
      setRows(acc.data || []);
      setDesigns(list);
      if (list.length > 0) setSelectedDesignId(list[0].design_id);
    } catch (e) {
      toast.error("Could not load your account data.");
      setRows([]);
      setDesigns([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleBuildPackage = async () => {
    if (!selectedDesignId) {
      toast.error("Pick a design first.");
      return;
    }
    setPkgBuilding(true);
    try {
      const r = await requestArchitectPackage(selectedDesignId);
      const { zip_base64, filename, contents, remaining_credits, size_bytes } = r.data || {};
      if (!zip_base64) throw new Error("No zip in response");
      const blob = await fetch(`data:application/zip;base64,${zip_base64}`).then((x) => x.blob());
      const url = URL.createObjectURL(blob);
      // V2.0.28 — Replace the silent auto-download with the Project
      // Delivery Dashboard. The user can trigger the download (or any
      // future per-file download) from there.
      const selected = (designs || []).find((d) => d.design_id === selectedDesignId);
      setDeliveryPackage({
        designName: selected?.name || selectedDesignId,
        designId: selectedDesignId,
        filename: filename || "architect-package.zip",
        zipBlobUrl: url,
        sizeBytes: size_bytes || blob.size,
        contents: contents || {},
        remainingCredits: remaining_credits,
      });
    } catch (e) {
      if (e?.response?.status === 402) {
        const detail = e.response?.data?.detail || {};
        toast.error(
          `Need ${detail.package_cost_credits || 2} credits — you have ${detail.current_credits ?? 0}. Buy more from any design.`,
        );
      } else {
        toast.error("Could not build the package. Try again.");
      }
    } finally {
      setPkgBuilding(false);
    }
  };

  // Revoke the blob URL on dismiss so we don't leak memory across builds.
  const dismissDelivery = () => {
    if (deliveryPackage?.zipBlobUrl) {
      try { URL.revokeObjectURL(deliveryPackage.zipBlobUrl); } catch (_) { /* noop */ }
    }
    setDeliveryPackage(null);
  };

  const latestVersion = rows && rows.length > 0 ? rows[0].tos_version : null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="account-page">
      <HeaderBar />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        {deliveryPackage ? (
          <ProjectDeliveryDashboard
            {...deliveryPackage}
            onClose={dismissDelivery}
          />
        ) : (
        <>
        <Link
          to="/dashboard"
          data-testid="account-back-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-brass-400 transition-colors mb-8"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          // back to projects
        </Link>

        <h1
          data-testid="account-page-title"
          className="font-mono text-2xl md:text-3xl text-brass-400 uppercase tracking-wide mb-2"
        >
          // Account
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-10">
          Identity · ToS audit history
        </p>

        {/* Identity card */}
        <section
          data-testid="account-identity-card"
          className="border border-zinc-800 bg-zinc-950 p-5 mb-10 flex items-center gap-4"
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-14 h-14 border border-zinc-700 object-cover"
              data-testid="account-avatar"
            />
          ) : (
            <div className="w-14 h-14 border border-zinc-700 bg-zinc-800 grid place-items-center text-lg font-mono">
              {user?.name?.[0] || "U"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div
              className="font-display text-lg text-zinc-100 truncate"
              data-testid="account-name"
            >
              {user?.name || "—"}
            </div>
            <div
              className="font-mono text-xs text-zinc-400 truncate"
              data-testid="account-email"
            >
              {user?.email || "—"}
            </div>
          </div>
          {latestVersion && (
            <div
              data-testid="account-current-tos-version"
              className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-brass-500/40 bg-brass-950/30 text-brass-300 font-mono text-[10px] uppercase tracking-widest"
              title="The most recent ToS version you accepted"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              ToS · {latestVersion}
            </div>
          )}
        </section>

        {/* V2.0.23 — Architect-ready Package banner */}
        <section
          data-testid="architect-package-banner"
          className="border border-brass-500/40 bg-gradient-to-br from-brass-950/40 via-zinc-950 to-zinc-950 p-6 mb-10"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className="hidden md:flex w-12 h-12 border border-brass-500/40 bg-brass-950/50 grid place-items-center shrink-0">
              <Package className="w-6 h-6 text-brass-400" />
            </div>
            <div className="flex-1 min-w-[280px]">
              <h2
                data-testid="architect-package-title"
                className="font-mono text-base text-brass-300 uppercase tracking-wide mb-1"
              >
                // Architect-ready Package
              </h2>
              <p className="text-sm text-zinc-300 leading-relaxed mb-3">
                Bundle your design into one zip your{" "}
                <strong className="text-zinc-100">licensed architect or GC</strong>{" "}
                can open immediately: clean floor-plan PDF · IFC 4 BIM file (Revit / ArchiCAD ready) · signed acceptance log.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  Design
                </label>
                <select
                  data-testid="architect-package-design-select"
                  value={selectedDesignId}
                  onChange={(e) => setSelectedDesignId(e.target.value)}
                  disabled={!designs || designs.length === 0 || pkgBuilding}
                  className="bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-200 px-3 py-1.5 font-mono text-xs focus:outline-none focus:border-brass-500 min-w-[240px] disabled:opacity-50"
                >
                  {(!designs || designs.length === 0) && (
                    <option value="">No designs yet</option>
                  )}
                  {(designs || []).map((d) => (
                    <option key={d.design_id} value={d.design_id}>
                      {d.name || d.design_id}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  data-testid="architect-package-build-btn"
                  onClick={handleBuildPackage}
                  disabled={pkgBuilding || !selectedDesignId}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-brass-600 hover:bg-brass-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-mono text-[10px] uppercase tracking-widest transition-colors"
                >
                  {pkgBuilding ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Assembling
                    </>
                  ) : (
                    <>
                      <Download className="w-3.5 h-3.5" /> Build Package · 2 credits
                    </>
                  )}
                </button>
                <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  <Sparkles className="w-3 h-3 text-brass-400" />
                  Acceptance log free
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Audit history */}
        <section data-testid="account-tos-history">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-base text-zinc-100 uppercase tracking-wide border-l-2 border-brass-400 pl-3">
              // Terms-of-Service Acceptance Log
            </h2>
            <Link
              to="/terms"
              target="_blank"
              rel="noreferrer"
              data-testid="account-view-current-terms-link"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-brass-400 hover:text-brass-300"
            >
              View current Terms
              <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          <p className="text-zinc-400 text-xs leading-relaxed mb-5 max-w-2xl">
            Every time you tick the &ldquo;I agree&rdquo; checkbox above a
            paid action (PDF/IFC download, Studio-tier purchase, AI Render
            unlock) we log the event below. Use this audit trail when
            handing the file off to your licensed architect or general
            contractor — it&apos;s your provable record that the design was
            understood as conceptual.
          </p>

          {loading && (
            <div
              data-testid="account-history-loading"
              className="border border-zinc-800 p-8 grid place-items-center text-zinc-500 font-mono text-xs"
            >
              <Loader2 className="w-5 h-5 animate-spin mb-2 text-brass-400" />
              Loading your acceptance history…
            </div>
          )}

          {!loading && rows && rows.length === 0 && (
            <div
              data-testid="account-history-empty"
              className="border border-dashed border-zinc-800 p-10 text-center"
            >
              <Inbox className="w-7 h-7 text-zinc-600 mx-auto mb-3" />
              <p className="font-mono text-xs text-zinc-400 uppercase tracking-widest mb-1">
                No acceptances yet
              </p>
              <p className="text-sm text-zinc-500 max-w-md mx-auto">
                You haven&apos;t agreed to the Terms of Service on any paid
                action yet. The next time you download a PDF, export an IFC,
                or unlock a Studio-tier package, the event will be recorded
                here.
              </p>
            </div>
          )}

          {!loading && rows && rows.length > 0 && (
            <div
              data-testid="account-history-table"
              className="border border-zinc-800 overflow-hidden"
            >
              <div className="grid grid-cols-12 px-4 py-2 bg-zinc-900 border-b border-zinc-800 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                <div className="col-span-3">When</div>
                <div className="col-span-3">Surface</div>
                <div className="col-span-3">Design</div>
                <div className="col-span-2">Version</div>
                <div className="col-span-1 text-right">IP</div>
              </div>
              {rows.map((r) => (
                <div
                  key={r.id}
                  data-testid={`account-history-row-${r.id}`}
                  className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-zinc-900 last:border-b-0 hover:bg-zinc-900/40 text-sm"
                >
                  <div className="col-span-3 font-mono text-xs text-zinc-300">
                    {formatDate(r.accepted_at)}
                  </div>
                  <div className="col-span-3 text-zinc-200">
                    <span className="inline-flex items-center gap-1.5">
                      <FileSignature className="w-3 h-3 text-brass-400" />
                      {SURFACE_LABEL[r.surface] || r.surface}
                    </span>
                  </div>
                  <div className="col-span-3 font-mono text-xs text-zinc-400 truncate">
                    {r.design_id ? (
                      <Link
                        to={`/designs/${r.design_id}`}
                        className="text-brass-400 hover:text-brass-300 underline underline-offset-2 truncate"
                        title={r.design_id}
                      >
                        {r.design_id}
                      </Link>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </div>
                  <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    {r.tos_version}
                  </div>
                  <div
                    className="col-span-1 text-right font-mono text-[10px] text-zinc-500 truncate"
                    title={`${r.ip_address || "—"} · ${r.user_agent || ""}`}
                  >
                    {r.ip_address || "—"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* V2.0.54 — Danger Zone: self-serve account deletion. */}
        <DangerZone />
        </>
        )}
      </main>
    </div>
  );
}

/**
 * V2.0.54-55 — Self-serve account deletion. The flow is:
 *   1. User clicks "Delete Account" — we fetch the counts of what
 *      would be removed.
 *   2. A shadcn AlertDialog opens listing every record bucket + total.
 *   3. To enable the final confirm button the user must type DELETE
 *      verbatim.
 *   4. Submit → DELETE /api/auth/me → clear local auth → redirect home.
 */
function DangerZone() {
  const { logout: clearLocalAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const COUNT_LABELS = [
    ["designs", "designs"],
    ["project_unlocks", "paid project unlocks"],
    ["renders", "AI renders"],
    ["referrals", "referral records"],
    ["code_finder_lookups", "code-finder lookups"],
    ["code_finder_leads", "lead-capture entries"],
    ["chip_events", "telemetry events"],
    ["feedback", "feedback submissions"],
    ["user_sessions", "active sessions"],
  ];

  const handleOpen = async () => {
    setLoadingPreview(true);
    setPhrase("");
    setOpen(true);
    try {
      const { data } = await previewAccountDeletion();
      setPreview(data);
    } catch {
      toast.error("Could not load deletion preview — please try again");
      setOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDelete = async () => {
    if (phrase.trim() !== "DELETE") {
      toast.error("Type DELETE in capital letters to confirm");
      return;
    }
    setBusy(true);
    try {
      const { data } = await deleteMyAccount();
      const removed = data?.removed || {};
      const total = Object.values(removed).reduce((s, n) => s + (Number(n) || 0), 0);
      toast.success(`Account deleted. ${total} records removed.`);
      try { await clearLocalAuth?.(); } catch { /* ignore — page redirect resets state */ }
      window.location.href = "/";
    } catch {
      setBusy(false);
      toast.error("Could not delete account — please email info@blueprintaillc.com");
    }
  };

  const counts = preview?.counts || {};
  const total = preview?.total ?? 0;
  const visibleRows = COUNT_LABELS.filter(([k]) => (counts[k] || 0) > 0);

  return (
    <section
      data-testid="account-danger-zone"
      className="mt-14 border border-red-900/60 bg-red-950/20 p-6"
    >
      <h2 className="font-mono text-[11px] uppercase tracking-widest text-red-300 mb-2 inline-flex items-center gap-2">
        <AlertOctagon className="w-3.5 h-3.5" />
        Danger Zone
      </h2>
      <p className="text-sm text-zinc-300 leading-relaxed">
        Deleting your account is permanent. It removes every design you&apos;ve
        created, every project unlock, every referral record, and every
        active session. Payment receipts retained for tax compliance are
        anonymised. There is no undo.
      </p>
      <button
        type="button"
        data-testid="open-delete-account-btn"
        onClick={handleOpen}
        className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-red-700 text-red-200 hover:bg-red-900/40 font-mono text-[11px] uppercase tracking-widest transition-colors"
      >
        Delete Account
      </button>

      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent
          data-testid="delete-account-alert"
          className="border-red-900/80 bg-zinc-950"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="inline-flex items-center gap-2 text-red-200">
              <AlertOctagon className="w-5 h-5" />
              Are you ABSOLUTELY sure?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-300">
              The action below cannot be undone. Confirming will permanently
              remove the following records tied to your account:
            </AlertDialogDescription>
          </AlertDialogHeader>

          {loadingPreview ? (
            <div className="flex items-center gap-2 py-4 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Counting records…
            </div>
          ) : (
            <div
              data-testid="delete-account-counts"
              className="my-2 border border-zinc-800 bg-zinc-900/60 divide-y divide-zinc-800"
            >
              {visibleRows.length === 0 ? (
                <div className="px-3 py-2 text-sm text-zinc-400">
                  No records tied to this account — only the user record will be removed.
                </div>
              ) : (
                visibleRows.map(([k, label]) => (
                  <div
                    key={k}
                    data-testid={`delete-account-count-${k}`}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <span className="text-zinc-300">{label}</span>
                    <span className="font-mono text-zinc-100">{counts[k]}</span>
                  </div>
                ))
              )}
              <div className="flex items-center justify-between px-3 py-2 bg-red-950/40">
                <span className="font-mono text-[11px] uppercase tracking-widest text-red-200">Total</span>
                <span
                  data-testid="delete-account-count-total"
                  className="font-mono text-red-100 font-bold"
                >
                  {total}
                </span>
              </div>
            </div>
          )}

          <div className="mt-2 space-y-2">
            <label className="block font-mono text-[10px] uppercase tracking-widest text-red-300">
              Type <span className="text-red-100">DELETE</span> to enable the confirm button
            </label>
            <input
              type="text"
              data-testid="delete-account-confirm-input"
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder="DELETE"
              className="w-full bg-zinc-950 border border-red-800 text-zinc-50 px-3 py-2 font-mono text-sm focus:outline-none focus:border-red-500"
              autoFocus
              disabled={busy || loadingPreview}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-delete-account-btn"
              disabled={busy}
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-account-btn"
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={busy || loadingPreview || phrase.trim() !== "DELETE"}
              className="bg-red-700 hover:bg-red-600 text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertOctagon className="w-3.5 h-3.5" />}
              {busy ? "Deleting…" : "Yes, delete everything"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
